import { useLocalStorage } from '@vueuse/core';
import { boundingExtent } from 'ol/extent';
import type Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import type Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type MapEvent from 'ol/MapEvent';
import Cluster from 'ol/source/Cluster';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import { ref } from 'vue';

import { NoraLayerManager } from '#nora-ui/map/map/layer-managers/NoraLayerManager';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';

export interface ClusterOptions {
  distance?: number;
  minDistance?: number;
}

/**
 * ClusterableLayer provides clustering functionality for vector layers on the NoraMap.
 * It manages clustering settings, styles for clusters, and allows toggling clustering on and off.
 */
export abstract class ClusterableLayer extends NoraLayerManager {
  /**
   * The cluster source for the layer.
   */
  protected cluster: Cluster<Feature>;

  /**
   * The vector source and layer managed by this ClusterableLayer.
   */
  protected source: VectorSource;
  protected layer: VectorLayer<VectorSource>;

  /**
   * Cache for styles based on cluster size.
   */
  protected styleCache: Record<number, Style[]> = {};

  /** The distance in pixels between features, below which they will be clustered. */
  protected readonly INITIAL_CLUSTER_DISTANCE = 22;
  protected readonly INITIAL_MIN_DISTANCE_BETWEEN_CLUSTERS = 10;

  protected clusterDistance = this.INITIAL_CLUSTER_DISTANCE;

  /**
   * Indicates whether clustering is currently enabled, managed reactively.
   */
  protected isClusteringRef = ref<boolean>(false);

  /**
   * Default style settings for clusters.
   */
  protected CLUSTER_STROKE_COLOR = '#CCD8E0';
  protected CLUSTER_FILL_COLOR = '#092020';
  protected CLUSTER_TEXT_COLOR = '#CCD8E0';
  protected CLUSTER_ICON_COLOR = '#CCD8E0';
  protected CLUSTER_ICON_NAME = 'question_mark';

  /**
   * The z-index of the layer.
   */
  protected LAYER_Z_INDEX = 1000;

  /**
   * Indicates whether clustering is currently enabled.
   */
  get isClustered(): boolean {
    return this.isClusteringRef.value;
  }

  /**
   * Constructor initializes the ClusterableLayer with default sources and clustering settings.
   */
  constructor(
    protected readonly map: NoraMap,
  ) {
    super();

    this.source = new VectorSource();
    this.cluster = new Cluster({
      source: this.source,
      distance: this.INITIAL_CLUSTER_DISTANCE,
      minDistance: this.INITIAL_MIN_DISTANCE_BETWEEN_CLUSTERS,
    });

    this.layer = new VectorLayer({
      source: this.cluster,
      zIndex: this.LAYER_Z_INDEX,
      style: feature => this.getStyle(feature),
    });
  }

  override setup(): void | Promise<void> {
    this.setupClickHandler();
    this.setupClustering();
  }

  /**
   * Set clustering options such as distance and minimum distance.
   * @param options
   */
  setClusterSettings(options: ClusterOptions): void {
    if (typeof options.distance === 'number') {
      this.cluster.setDistance(options.distance);
    }

    if (typeof options.minDistance === 'number') {
      this.cluster.setMinDistance(options.minDistance);
    }
  }

  /**
   * Enable or disable clustering based on the provided boolean.
   * @param enabled
   */
  setClusterMode(enabled: boolean): void {
    if (enabled) {
      this.enableClustering();
    }
    else {
      this.disableClustering();
    }

    this.isClusteringRef.value = enabled;
  }

  /**
   * Enable clustering.
   */
  enableClustering(): void {
    this.applyClusterSource(true);
    this.isClusteringRef.value = true;
  }

  /**
   * Disable clustering.
   */
  disableClustering(): void {
    this.applyClusterSource(false);
    this.isClusteringRef.value = false;
  }

  /**
   * Render the Cluster source (on) or the raw source (off).
   *
   * When off the Cluster is detached with `setSource(null)` — otherwise OpenLayers keeps it
   * subscribed to the source and reclusters on every feature update even though it isn't drawn.
   */
  private applyClusterSource(enabled: boolean): void {
    if (enabled) {
      this.cluster.setSource(this.source);
      this.layer.setSource(this.cluster);
    }
    else {
      this.layer.setSource(this.source);
      this.cluster.setSource(null);
    }
  }

  /**
   * Toggle clustering on or off based on the current state.
   */
  toggleClustering(): void {
    this.setClusterMode(!this.isClustered);
  }

  /**
   * Get the vector layer managed by this ClusterableLayer.
   * @returns VectorLayer<VectorSource>
   */
  override getLayer(): VectorLayer<VectorSource> {
    return this.layer;
  }

  /**
   * Get the vector source managed by this layer.
   * @returns VectorSource
   */
  getSource(): VectorSource {
    return this.source;
  }

  /**
   * Get the style for a given feature, determining if it's a single feature or a cluster.
   * @param feature
   * @returns Style | Style[]
   */
  private getStyle(feature: FeatureLike): Style | Style[] {
    // With clustering off the raw source is rendered, so features have no 'features' member.
    if (feature.get('features') === undefined) {
      return this.getStyleForSingleFeature(feature);
    }

    const features = this.getFeatures(feature);
    const size = features.length;

    if (size === 1) {
      const firstFeature = features[0];
      return this.getStyleForSingleFeature(firstFeature);
    }

    return this.internalGetStyleForCluster(size);
  }

  /**
   * Get an empty style for features that should not be displayed.
   * @returns Style
   */
  protected getEmptyStyle(): Style {
    return new Style();
  }

  /**
   * Get the features contained within a cluster feature.
   * This can be overridden to filter features as needed.
   * @param feature
   * @returns FeatureLike[]
   */
  protected getFeatures(feature: FeatureLike): FeatureLike[] {
    const features = feature.get('features') as FeatureLike[] | undefined;

    return features ?? [];
  }

  /**
   * Get the style for a single feature.
   * This method should be overridden by subclasses to provide specific styles.
   * @param _feature
   * @returns Style | Style[]
   */
  protected getStyleForSingleFeature(_feature: FeatureLike): Style[] | Style {
    return new Style();
  }

  private setupClickHandler(): void {
    this.map.getOlMapOrThrow().on('click', (event) => {
      this.checkClusterClicks(event);
    });
  }

  setupClustering(): void {
    const LOCAL_STORAGE_KEY: string = `nora.map.clustering.state.${this.LAYER_ID}`;
    // Default on for these dense layers; a user's explicit toggle is still respected.
    this.isClusteringRef = useLocalStorage<boolean>(LOCAL_STORAGE_KEY, true);

    this.applyClusterSource(this.isClusteringRef.value);
  }

  private checkClusterClicks(
    event: MapBrowserEvent | MapEvent,
  ): void {
    const features = this.getClusterFeaturesForEvent(event);

    if (features === undefined || features.length <= 1) {
      return;
    }

    const extent = this.getExtentForFeatures(features);

    this.map.fitToExtent(extent);
  }

  private getClusterFeaturesForEvent(event: MapBrowserEvent | MapEvent): Feature[] | undefined {
    if (!('pixel' in event)) {
      return;
    }

    const features = this.map
      .getOlMapOrThrow()
      .getFeaturesAtPixel(event.pixel, {
        layerFilter: layer => layer === this.layer,
      });

    if (features.length === 0) {
      return;
    }

    const subFeatures: Feature[] = [];

    for (const feature of features) {
      const sub = feature.get('features') as Feature[] | undefined;

      if (sub && Array.isArray(sub)) {
        subFeatures.push(...sub);
      }
    }

    return subFeatures;
  }

  private getExtentForFeatures(features: Feature[]): number[] {
    return boundingExtent(
      features.map((feature) => {
        const geometry = feature.getGeometry();

        if (!geometry) {
          return [Number.NaN, Number.NaN];
        }

        const point: Point = geometry as Point;

        return point.getCoordinates();
      }),
    );
  }

  /**
   * Get the style for a cluster based on its size.
   * This method uses caching to optimize style retrieval.
   * @param size
   * @returns Style | Style[]
   */
  private internalGetStyleForCluster(size: number): Style[] | Style {
    let style = this.getStyleFromCache(size);

    if (!style) {
      let fontSize = '14px';
      let offset = 3;

      if (size > 9) {
        offset = 1;
      }
      if (size > 99) {
        fontSize = '12px';
      }
      if (size > 999) {
        fontSize = '10px';
      }

      style = this.getStyleForCluster(size, fontSize, offset);

      this.cacheStyle(size, style);
    }

    return style;
  }

  /**
   * Generate the style for a cluster given its size, font size, and offset.
   * This method can be overridden for custom cluster styles.
   * @param size
   * @param fontSize
   * @param offset
   * @returns Style[]
   */
  protected getStyleForCluster(
    size: number,
    fontSize: string,
    offset: number,
  ): Style[] {
    return [
      new Style({
        image: new CircleStyle({
          radius: 24,
          stroke: new Stroke({
            color: this.CLUSTER_STROKE_COLOR,
            width: 4,
          }),
          fill: new Fill({
            color: this.CLUSTER_FILL_COLOR,
          }),
        }),
        text: new Text({
          fill: new Fill({ color: this.CLUSTER_TEXT_COLOR }),
          font: `700 ${fontSize} Source Sans Pro`,
          text: size.toString(),
          textAlign: 'end',
          offsetX: offset * -1,
        }),
      }),
      new Style({
        text: new Text({
          text: this.CLUSTER_ICON_NAME,
          font: '14px "Material Icons"',
          fill: new Fill({ color: this.CLUSTER_ICON_COLOR }),
          textAlign: 'start',
          offsetX: offset,
        }),
      }),
    ];
  }

  /**
   * Retrieve a cached style for a given cluster size.
   * @param size
   * @returns Style[] | undefined
   */
  protected getStyleFromCache(size: number): Style[] | undefined {
    return this.styleCache[size];
  }

  protected cacheStyle(size: number, style: Style[]): void {
    this.styleCache[size] = style;
  }
}
